/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import { filterEmptyTableData } from "../../../../components/EditableTable/EditableTable";
import AdvancedIntraOralContent, {
  hasContentAdvancedIntraOral
} from "../../../Modals/Assessment/AdvancedIntraOralContent";

import PrintRow from "../PrintRow";
import { tl } from "../../../../components/translate";
import { SORT_ORDER } from "../../AssessmentForm/OPDComponents";
import { Assessment, ExtraOral } from "../../../../interfaces/AssessmentInterfaces";
import EyeAssessmentContent from "../../../Modals/Assessment/EyeAssessmentContent";
import { hasDiagnosis } from "../../../Modals/Assessment/DiagnosisRenderer";
import {
  DentalServiceContent,
  DiagnosisContent,
  ExtraOralContent,
  FollowUpContent,
  IntraOralContent,
  LabTestContent,
  MedicationContent,
  OtherServiceContent,
  QuestionnaireContent,
  SkinAnalysisContent,
  TabularDataContent,
  TextContent,
  TreatmentDoneContent,
  VitalsContent,
  XRayServiceContent
} from "./TabularPrintComponents";
import PrescriptionSummaryPrint from "../PrescriptionSummaryPrint";

// eslint-disable-next-line import/prefer-default-export
export const TabularPrintContent = ({
  componentName,
  assessment,
  assessmentLabels
}: {
  componentName: string;
  assessment: Assessment;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assessmentLabels: Record<string, any>;
}): React.JSX.Element | null => {
  const servicesAndTests = [...(assessment?.tests || []), ...(assessment?.services || [])];
  const tabularComponentData =
    assessment.tabularComponent && filterEmptyTableData(assessment.tabularComponent);

  switch (componentName) {
    case SORT_ORDER.RECORD_PRESCRIPTION: {
      const prescriptionText =
        assessment.prescriptionAudioSummary || assessment.prescriptionAudioTranscription;

      return prescriptionText ? <PrescriptionSummaryPrint text={prescriptionText} /> : null;
    }

    case SORT_ORDER.VITALS:
      return assessment?.vitals?.length > 0 ? (
        <PrintRow label={assessmentLabels.vitals || "Vitals"} nextLineChildren>
          <VitalsContent vitals={assessment.vitals} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.QUESTIONNAIRE:
      return assessment?.questionnaire?.length > 0 ? (
        <PrintRow label={assessmentLabels.questionnaire || "Questionnaire"} nextLineChildren>
          <QuestionnaireContent questionnaire={assessment.questionnaire} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.MEDICATION:
      return assessment?.medication?.length > 0 || assessment?.productMedication?.length > 0 ? (
        <PrintRow label={assessmentLabels.medication || "Medication"} nextLineChildren>
          <MedicationContent
            medications={[
              ...(assessment.medication ?? []),
              ...(assessment.productMedication ?? [])
            ]}
          />
        </PrintRow>
      ) : null;

    case SORT_ORDER.INTRA_ORAL:
      return assessment.intraOral &&
        (Object.keys(assessment.intraOral.primary).length !== 0 ||
          Object.keys(assessment.intraOral.permanent).length !== 0) ? (
        <PrintRow label={assessmentLabels.intraOral || "Intra Oral"} nextLineChildren hideColon>
          <IntraOralContent intraOralDoc={assessment.intraOral} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.EXTRA_ORAL:
      return Object.keys(assessment.extraOral || {}).some(
        (key) => !!assessment.extraOral[key as keyof ExtraOral]
      ) ? (
        <PrintRow
          label={assessmentLabels.extraOral || "Extra Oral Examination"}
          nextLineChildren
          hideColon
        >
          <ExtraOralContent extraOral={assessment.extraOral} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.ADVANCED_INTRA_ORAL:
      return assessment.advancedIntraOral &&
        hasContentAdvancedIntraOral(assessment.advancedIntraOral) ? (
        <PrintRow
          label={assessmentLabels.advancedIntraOral}
          shouldAvoidPageBreak
          nextLineChildren
          labelWidth="100%"
          hideColon
        >
          <AdvancedIntraOralContent assessmentDocument={assessment} mode="print" />
        </PrintRow>
      ) : null;

    case SORT_ORDER.EYE:
      return (assessment?.eye && Object.keys(assessment?.eye).length) > 0 ? (
        <PrintRow
          label={assessmentLabels.eye}
          shouldAvoidPageBreak
          nextLineChildren
          labelWidth="100%"
          hideColon
        >
          <EyeAssessmentContent eyeExaminationDoc={assessment?.eye} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.LAB_TEST:
      return servicesAndTests.length > 0 ? (
        <PrintRow label={assessmentLabels.labTest} nextLineChildren hideColon>
          <LabTestContent tests={servicesAndTests as any} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.OTHER_SERVICES:
      return assessment.otherServices?.length > 0 ? (
        <PrintRow
          label={assessmentLabels.otherServices || "Other Services"}
          nextLineChildren
          hideColon
        >
          <OtherServiceContent services={assessment.otherServices} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.DENTAL_SERVICES:
      return assessment.dentalServices?.length > 0 ? (
        <PrintRow
          label={assessmentLabels.dentalServices || "Dental Services"}
          nextLineChildren
          hideColon
        >
          <DentalServiceContent services={assessment.dentalServices} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.XRAY_SERVICES:
      return assessment.xrayServices?.length > 0 ? (
        <PrintRow
          label={assessmentLabels.xrayServices || "X Ray Services"}
          nextLineChildren
          hideColon
        >
          <XRayServiceContent services={assessment.xrayServices} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.TREATMENT_DONE:
      return assessment?.treatmentDone?.length ? (
        <PrintRow
          label={assessmentLabels.treatmentDone || "Treatment Done"}
          nextLineChildren
          hideColon
        >
          <TreatmentDoneContent treatmentDone={assessment.treatmentDone} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.DIAGNOSIS:
      return hasDiagnosis(assessment.diagnosis) ? (
        <PrintRow label={assessmentLabels.diagnosis || "Diagnosis"} nextLineChildren hideColon>
          <DiagnosisContent diagnosis={assessment.diagnosis} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.TABULAR:
      return assessment.tabularComponent && !!tabularComponentData ? (
        <PrintRow
          label={assessmentLabels.tabularComponent || tabularComponentData.title}
          nextLineChildren
        >
          <TabularDataContent tableState={tabularComponentData} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.FOLLOWUP:
      return assessment.followup ? (
        <PrintRow label={assessmentLabels.followup || "Follow Up"} labelWidth={160}>
          <FollowUpContent followup={assessment.followup} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.EXAMINATION_OR_STATUS:
      return (
        <>
          {assessment.assessment?.length > 0 && (
            <TextContent
              label={assessmentLabels.examination || "Examination"}
              value={assessment.assessment.trim()}
            />
          )}
          {assessment.assessmentImage && (
            <PrintRow labelWidth={160} label={tl("assessment.image")}>
              <img
                src={assessment.assessmentImage}
                alt="AssessmentImage"
                className="assessment-image"
              />
            </PrintRow>
          )}
        </>
      );

    // one liner and text only components
    case SORT_ORDER.CHIEF_COMPLAINTS:
      return assessment.symptoms?.length > 0 && Array.isArray(assessment.symptoms) ? (
        <TextContent
          label={assessmentLabels.chiefComplaints || "Chief Complaints"}
          value={assessment.symptoms.map((s) => s.symptom).join(", ")}
        />
      ) : null;

    case SORT_ORDER.ADVICE:
      return assessment.advice ? (
        <TextContent value={assessment.advice} label={assessmentLabels.advice || "Advice"} />
      ) : null;

    case SORT_ORDER.INVESTIGATION:
      return assessment.investigation ? (
        <TextContent
          value={assessment.investigation}
          label={assessmentLabels.investigation || "Investigation"}
        />
      ) : null;

    case SORT_ORDER.DERMASCOPY:
      return assessment.dermascopy ? (
        <TextContent
          value={assessment.dermascopy}
          label={assessmentLabels.dermascopy || "Dermascopy"}
        />
      ) : null;

    case SORT_ORDER.PAST_HISTORY_OF_ALLERGY:
      return assessment.pastHistoryOfAllergy ? (
        <TextContent
          value={assessment.pastHistoryOfAllergy}
          label={assessmentLabels.pastHistoryOfAllergy || "Past History of Allergy"}
        />
      ) : null;

    case SORT_ORDER.HOPI:
      return assessment.hopi?.length > 0 ? (
        <TextContent value={assessment.hopi} label={assessmentLabels.hopi || "H.O.P.I."} />
      ) : null;

    case SORT_ORDER.PROVISIONAL_DIAGNOSIS:
      return assessment.provisionalDiagnosis?.length > 0 ? (
        <TextContent
          value={assessment.provisionalDiagnosis}
          label={assessmentLabels.provisionalDiagnosis || "Provisional Diagnosis"}
        />
      ) : null;

    case SORT_ORDER.TREATMENT_PLAN:
      return assessment.treatmentPlan?.length > 0 ? (
        <TextContent
          value={assessment.treatmentPlan}
          label={assessmentLabels.treatmentPlan || "Treatment Plan"}
        />
      ) : null;

    case SORT_ORDER.TREATMENT_GIVEN:
      return assessment.treatmentGiven?.length > 0 ? (
        <TextContent
          value={assessment.treatmentGiven}
          label={assessmentLabels.treatmentGiven || "Treatment Given"}
        />
      ) : null;

    case SORT_ORDER.OTHERS:
      return assessment?.others ? (
        <TextContent value={assessment.others} label={assessmentLabels.others || "Others"} />
      ) : null;

    case SORT_ORDER.OTHER1:
      return assessment?.other1 ? (
        <TextContent value={assessment.other1} label={assessmentLabels.other1 || "Other1"} />
      ) : null;

    case SORT_ORDER.OTHER2:
      return assessment?.other2 ? (
        <TextContent value={assessment.other2} label={assessmentLabels.other2 || "Other2"} />
      ) : null;

    case SORT_ORDER.OTHER3:
      return assessment?.other3 ? (
        <TextContent value={assessment.other3} label={assessmentLabels.other3 || "Other3"} />
      ) : null;
    case SORT_ORDER.SKIN_ANALYSIS:
      return assessment?.skinAnalysis?.length > 0 ? (
        <PrintRow
          label={assessmentLabels.skinAnalysis || "Skin Analysis"}
          nextLineChildren
          hideColon
        >
          <SkinAnalysisContent skinAnalysis={assessment.skinAnalysis} />
        </PrintRow>
      ) : null;

    default:
      return null;
  }
};
